// Settings card (Session AUH, email system Phase 7): how the platform chases.
//
// Three things, each its own save through a two-step confirm, audited server side:
//   1. THE LADDERS: the working days between each email step of every editable ladder (prospect quotes, customer
//      information, customer decisions, suppliers, examining bodies, anything else). The two DEBTOR ladders are the
//      Director's own rules (item 3b) and are shown, never edited here.
//   2. SUPPLIERS: a supplier the office never chases, or never chases firmly (matched by email, domain or name).
//   3. THE DEBT RECOVERY CONTACT, ADMIN ONLY: who a referral pack goes to. Named to a customer only in a final notice before
//      legal action or a notice of referral. "Use the default" puts the Director's default back.
// office and admin see the card; only an admin can change the recovery contact (the route refuses anyone else).
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import type { ChaseReferenceRate, ChaseSettings, ChaseSupplierFlag } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { useAuth } from "../../lib/auth";
import { ConfirmButton } from "../../components/ConfirmButton";
import { formatDateLongUtc, formatDateTime } from "../../lib/format";
import { Loader } from "../../components/Loader";

const linkBtn = "font-menu text-xs tracking-wide text-ele-blue hover:text-ele-strong disabled:opacity-50";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const fieldCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 font-body text-sm text-ele-strong outline-none focus:border-ele-green";
const saveCls = "rounded-ele border border-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-green";

/** "2, 5, 10" -> [2, 5, 10]; null when it does not read as whole working days. Pure. */
export function parseSteps(text: string): number[] | null {
  const parts = text
    .split(/[\s,]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0 || parts.length > 5) return null;
  const nums = parts.map((p) => (/^\d{1,2}$/.test(p) ? Number(p) : NaN));
  return nums.every((n) => Number.isInteger(n) && n >= 1 && n <= 60) ? nums : null;
}

/** Every 30 June and 31 December from 2024 up to today, newest first: a reference rate is a fact about a past day. */
function halfYearDates(now: Date = new Date()): string[] {
  const today = now.toISOString().slice(0, 10);
  const out: string[] = [];
  for (let y = now.getFullYear(); y >= 2024; y--) for (const d of [`${y}-12-31`, `${y}-06-30`]) if (d <= today) out.push(d);
  return out;
}

function stepsText(steps: number[]): string {
  return steps.join(", ");
}

export function ChaseSettingsCard({ refreshKey }: { refreshKey?: number }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [view, setView] = useState<ChaseSettings | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [ladderText, setLadderText] = useState<Record<string, string>>({});
  const [suppliers, setSuppliers] = useState<ChaseSupplierFlag[]>([]);
  const [recName, setRecName] = useState("");
  const [recEmail, setRecEmail] = useState("");
  const [rates, setRates] = useState<ChaseReferenceRate[]>([]);
  const [rateDate, setRateDate] = useState("");
  const [rateText, setRateText] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  function adopt(v: ChaseSettings) {
    setView(v);
    setLadderText(Object.fromEntries(v.ladders.map((l) => [l.key, stepsText(l.steps)])));
    setSuppliers(v.suppliers);
    setRecName(v.recovery.name ?? "");
    setRecEmail(v.recovery.email ?? "");
    setRates(v.lateInterest?.referenceRates ?? []);
    setRateDate("");
    setRateText("");
  }

  useEffect(() => {
    let live = true;
    setState("loading");
    Promise.resolve()
      .then(() => api.chases.settings())
      .then((v) => {
        if (!live) return;
        adopt(v);
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  async function run(key: string, fn: () => Promise<unknown>, done: string, fallback: string) {
    setBusy(key);
    setError(null);
    setMessage(null);
    try {
      await fn();
      adopt(await api.chases.settings());
      setMessage(done);
    } catch (e) {
      setError(friendlyApiError(e, fallback));
    } finally {
      setBusy(null);
    }
  }

  const editable = view?.ladders.filter((l) => l.editable) ?? [];
  const fixed = view?.ladders.filter((l) => !l.editable) ?? [];
  const ladderEdits: Record<string, number[] | null> = {};
  let ladderInvalid = false;
  for (const l of editable) {
    const parsed = parseSteps(ladderText[l.key] ?? "");
    if (!parsed) ladderInvalid = true;
    else if (stepsText(parsed) !== stepsText(l.steps)) ladderEdits[l.key] = stepsText(parsed) === stepsText(l.defaultSteps) ? null : parsed;
  }
  const ladderChanged = Object.keys(ladderEdits).length > 0;
  const suppliersChanged = view ? JSON.stringify(suppliers) !== JSON.stringify(view.suppliers) : false;
  const suppliersValid = suppliers.every((s) => s.match.trim().length >= 2);

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Chasing">
      <h2 className="mb-2 font-heading text-lg text-ele-blue">Chasing</h2>
      <p className="mb-3 text-sm text-ele-body">
        How long the platform waits before each chase, in working days after the last one. Each chase is drafted on the morning it is
        due and sent only when someone presses Send.
      </p>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !view ? (
        <p role="status" className="text-sm text-ele-strong">
          Could not load the chase settings.
        </p>
      ) : (
        <div className="space-y-5">
          <div>
            <h3 className="mb-2 font-menu text-xs uppercase tracking-widest text-ele-body">Ladders</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {editable.map((l) => {
                const bad = parseSteps(ladderText[l.key] ?? "") === null;
                return (
                  <label key={l.key} className="block">
                    <span className={labelCls}>{l.label}</span>
                    <input
                      aria-label={`${l.label} steps`}
                      value={ladderText[l.key] ?? ""}
                      onChange={(e) => setLadderText((t) => ({ ...t, [l.key]: e.target.value }))}
                      className={`${fieldCls} ${bad ? "border-ele-danger" : ""}`}
                    />
                    <span className="mt-1 block text-xs text-ele-body">
                      {l.description} Default {stepsText(l.defaultSteps)}. After the last: {l.after}.
                    </span>
                  </label>
                );
              })}
            </div>
            {ladderInvalid && <p className="mt-2 text-xs text-ele-danger">Each ladder needs one to five whole numbers of working days, 1 to 60, separated by commas.</p>}
            {fixed.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs text-ele-body">
                {fixed.map((l) => (
                  <li key={l.key}>
                    <span className="text-ele-strong">{l.label}</span>: {stepsText(l.steps)} working days. {l.description} Fixed.
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-xs text-ele-body">
              {view.ladderUpdatedAt ? `Last changed ${formatDateTime(view.ladderUpdatedAt)}${view.ladderUpdatedByName ? ` by ${view.ladderUpdatedByName}` : ""}.` : "These are the defaults."}
            </p>
            {ladderChanged && !ladderInvalid && (
              <div className="mt-2">
                <ConfirmButton
                  onConfirm={() => void run("ladder", () => api.chases.setLadders(ladderEdits, false), "Saved. Open chases follow the new ladder from their next step.", "Could not save the ladders.")}
                  label="Save the ladders"
                  confirmLabel="Every later chase follows them. Press again"
                  busy={busy === "ladder"}
                  busyLabel="Saving..."
                  disabled={busy != null}
                  className={saveCls}
                  armedClassName="!text-ele-green font-semibold"
                />
              </div>
            )}
          </div>

          <div>
            <h3 className="mb-2 font-menu text-xs uppercase tracking-widest text-ele-body">Suppliers</h3>
            <p className="mb-2 text-xs text-ele-body">A supplier to leave alone, or to chase only softly. Match by email address, web domain, or the name on the purchase order.</p>
            {suppliers.length === 0 && <p className="text-xs text-ele-body">Every supplier is chased on the supplier ladder.</p>}
            <ul className="space-y-2">
              {suppliers.map((s, i) => (
                <li key={i} className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                  <input
                    aria-label={`Supplier ${i + 1}`}
                    value={s.match}
                    maxLength={200}
                    onChange={(e) => setSuppliers((all) => all.map((x, j) => (j === i ? { ...x, match: e.target.value } : x)))}
                    className={`${fieldCls} sm:flex-1`}
                  />
                  <label className="flex items-center gap-2 text-xs text-ele-body">
                    <input type="checkbox" checked={s.doNotChase} onChange={(e) => setSuppliers((all) => all.map((x, j) => (j === i ? { ...x, doNotChase: e.target.checked } : x)))} className="h-4 w-4 accent-ele-green" />
                    Never chase
                  </label>
                  <label className="flex items-center gap-2 text-xs text-ele-body">
                    <input type="checkbox" checked={s.doNotChaseHard} onChange={(e) => setSuppliers((all) => all.map((x, j) => (j === i ? { ...x, doNotChaseHard: e.target.checked } : x)))} className="h-4 w-4 accent-ele-green" />
                    Never firmly
                  </label>
                  <button type="button" className={linkBtn} onClick={() => setSuppliers((all) => all.filter((_, j) => j !== i))}>
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" className={`mt-2 ${linkBtn}`} onClick={() => setSuppliers((all) => [...all, { match: "", doNotChase: false, doNotChaseHard: true }])}>
              Add a supplier
            </button>
            {suppliersChanged && (
              <div className="mt-2">
                <ConfirmButton
                  onConfirm={() => void run("suppliers", () => api.chases.setSuppliers(suppliers.map((s) => ({ ...s, match: s.match.trim() })), false), "Saved.", "Could not save the supplier list.")}
                  label="Save the supplier list"
                  confirmLabel="Press again to save"
                  busy={busy === "suppliers"}
                  busyLabel="Saving..."
                  disabled={busy != null || !suppliersValid}
                  className={saveCls}
                  armedClassName="!text-ele-green font-semibold"
                />
              </div>
            )}
          </div>

          <div>
            <h3 className="mb-2 font-menu text-xs uppercase tracking-widest text-ele-body">Debt recovery contact</h3>
            <p className="mb-2 text-xs text-ele-body">
              Who an unpaid debt is referred to, after the final notice before legal action has gone and its pay-by date has passed. Named to the
              customer only in that notice and the notice of referral.
            </p>
            <p className="text-sm text-ele-strong" data-testid="chase-recovery-current">
              {view.recovery.name ?? "None"}
              {view.recovery.email ? ` (${view.recovery.email})` : " (no email: the referral pack is drafted to our own mailbox)"}
            </p>
            <p className="text-xs text-ele-body">
              {view.recovery.isDefault ? "This is the default." : "This is the office's own choice."}
              {view.recovery.updatedAt ? ` Last changed ${formatDateTime(view.recovery.updatedAt)}${view.recovery.updatedByName ? ` by ${view.recovery.updatedByName}` : ""}.` : ""}
            </p>
            {isAdmin ? (
              <div className="mt-2 space-y-2">
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="block">
                    <span className={labelCls}>Name</span>
                    <input aria-label="Debt recovery name" value={recName} maxLength={200} onChange={(e) => setRecName(e.target.value)} className={fieldCls} />
                  </label>
                  <label className="block">
                    <span className={labelCls}>Email</span>
                    <input aria-label="Debt recovery email" value={recEmail} maxLength={200} onChange={(e) => setRecEmail(e.target.value)} className={fieldCls} />
                  </label>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {(recName.trim() !== (view.recovery.name ?? "") || recEmail.trim() !== (view.recovery.email ?? "")) && recName.trim() && (
                    <ConfirmButton
                      onConfirm={() => void run("recovery", () => api.chases.setRecovery(recName.trim(), recEmail.trim() || null, false), "Saved. Later referrals go to this contact.", "Could not save the debt recovery contact.")}
                      label="Save the contact"
                      confirmLabel="Later referrals go here. Press again"
                      busy={busy === "recovery"}
                      busyLabel="Saving..."
                      disabled={busy != null}
                      className={saveCls}
                      armedClassName="!text-ele-green font-semibold"
                    />
                  )}
                  {!view.recovery.isDefault && (
                    <ConfirmButton
                      onConfirm={() => void run("recovery", () => api.chases.setRecovery(null, null, false), "The default contact is back.", "Could not restore the default.")}
                      label="Use the default"
                      confirmLabel="Press again"
                      busy={busy === "recovery"}
                      busyLabel="Saving..."
                      disabled={busy != null}
                      className={linkBtn}
                      armedClassName="!text-ele-green font-semibold"
                    />
                  )}
                </div>
              </div>
            ) : (
              <p className="mt-1 text-xs text-ele-body">Only an admin can change it.</p>
            )}
          </div>

          {view.lateInterest && (
            <div>
              <h3 className="mb-2 font-menu text-xs uppercase tracking-widest text-ele-body">Late payment reference rates</h3>
              <p className="mb-2 text-xs text-ele-body">
                The final notice before legal action claims late payment compensation and statutory interest at {view.lateInterest.upliftPct}% above the
                Bank of England rate at the close of the 30 June or 31 December before the debt fell overdue, for a customer with a company number recorded.
                With no rate recorded for the date a debt needs, the letter claims no interest.
              </p>
              {rates.length === 0 ? (
                <p className="text-sm text-ele-strong" data-testid="chase-late-rates-none">No reference rates recorded.</p>
              ) : (
                <ul className="space-y-1 text-sm text-ele-strong" data-testid="chase-late-rates">
                  {rates.map((r) => (
                    <li key={r.date} className="flex flex-wrap items-center gap-3">
                      <span>
                        {formatDateLongUtc(`${r.date}T00:00:00Z`)}: {r.baseRatePct}%
                      </span>
                      {isAdmin && (
                        <button type="button" className={linkBtn} disabled={busy != null} onClick={() => setRates(rates.filter((x) => x.date !== r.date))}>
                          Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {view.lateInterest.updatedAt && (
                <p className="text-xs text-ele-body">
                  Last changed {formatDateTime(view.lateInterest.updatedAt)}
                  {view.lateInterest.updatedByName ? ` by ${view.lateInterest.updatedByName}` : ""}.
                </p>
              )}
              {isAdmin ? (
                <div className="mt-2 space-y-2">
                  <div className="grid gap-2 sm:grid-cols-3">
                    <label className="block">
                      <span className={labelCls}>Date</span>
                      <select aria-label="Reference rate date" value={rateDate} onChange={(e) => setRateDate(e.target.value)} className={fieldCls}>
                        <option value="">Choose</option>
                        {halfYearDates().map((d) => (
                          <option key={d} value={d}>
                            {formatDateLongUtc(`${d}T00:00:00Z`)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className={labelCls}>Bank of England rate (%)</span>
                      <input aria-label="Bank of England rate" inputMode="decimal" value={rateText} maxLength={6} onChange={(e) => setRateText(e.target.value)} className={fieldCls} />
                    </label>
                    <div className="flex items-end">
                      <button
                        type="button"
                        className={linkBtn}
                        disabled={!rateDate || !/^\d{1,2}(?:\.\d{1,4})?$/.test(rateText.trim()) || busy != null}
                        onClick={() => {
                          const pct = Number(rateText.trim());
                          setRates([...rates.filter((x) => x.date !== rateDate), { date: rateDate, baseRatePct: pct }].sort((a, b) => a.date.localeCompare(b.date)));
                          setRateDate("");
                          setRateText("");
                        }}
                      >
                        Add to the list
                      </button>
                    </div>
                  </div>
                  {JSON.stringify(rates) !== JSON.stringify(view.lateInterest.referenceRates) && (
                    <ConfirmButton
                      onConfirm={() => void run("lateRates", () => api.chases.setLateInterestRates(rates, false), "Saved. Later final notices read these rates.", "Could not save the reference rates.")}
                      label="Save the rates"
                      confirmLabel="Later final notices claim from these. Press again"
                      busy={busy === "lateRates"}
                      busyLabel="Saving..."
                      disabled={busy != null}
                      className={saveCls}
                      armedClassName="!text-ele-green font-semibold"
                    />
                  )}
                </div>
              ) : (
                <p className="mt-1 text-xs text-ele-body">Only an admin can change them.</p>
              )}
            </div>
          )}

          {error && (
            <p role="alert" className="text-sm text-ele-danger">
              {error}
            </p>
          )}
          {message && (
            <p role="status" className="text-sm text-ele-strong">
              {message}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
